/**
 * Dashboard metrics — server state.
 *
 * A short `staleTime`: these are counts of work in progress, and several people
 * work the same queues at once, so a figure that is a minute stale is misleading
 * in the one place staff look to decide what to do next. Not zero either — the
 * dashboard is the landing screen, and refetching five aggregates on every
 * back-navigation is a cost with no reader.
 */

import { useQuery } from '@tanstack/react-query';
import { fetchDashboardMetrics } from '../services/api/metrics';
import type { MetricsRange } from '../services/api/metrics';
import { metricsKeys } from './queryKeys';

const METRICS_STALE_TIME_MS = 30 * 1000;

export { metricsKeys };

/**
 * The dashboard's figures, over a window.
 *
 * Called from two components on the same screen; React Query dedupes on the key,
 * so that is one request rather than two. Passing the range down as a prop and
 * letting each read it is simpler than lifting the data itself, and it keeps
 * each card's own loading and error states where they belong.
 */
export function useDashboardMetrics(range: MetricsRange = {}) {
  return useQuery({
    queryKey: metricsKeys.dashboard(range.from, range.to),
    queryFn: () => fetchDashboardMetrics(range),
    staleTime: METRICS_STALE_TIME_MS,
  });
}
